import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { getMe, type AuthUser } from '../api';

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  login: (token: string, user: AuthUser) => void;
  logout: () => void;
  refresh: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser]   = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('rfo_token');
    const storedUser = localStorage.getItem('rfo_user');
    if (stored && storedUser) {
      try {
        setToken(stored);
        setUser(JSON.parse(storedUser));
        // Hent live state fra backend (verified, status kan være ændret siden sidste login)
        getMe().then(fresh => {
          setUser(fresh);
          localStorage.setItem('rfo_user', JSON.stringify(fresh));
        }).catch(() => {
          // Token ugyldigt — log ud
          localStorage.removeItem('rfo_token');
          localStorage.removeItem('rfo_user');
          setToken(null);
          setUser(null);
        }).finally(() => setLoading(false));
      } catch {
        localStorage.removeItem('rfo_token');
        localStorage.removeItem('rfo_user');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  function login(newToken: string, newUser: AuthUser) {
    localStorage.setItem('rfo_token', newToken);
    localStorage.setItem('rfo_user', JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  }

  function logout() {
    localStorage.removeItem('rfo_token');
    localStorage.removeItem('rfo_user');
    setToken(null);
    setUser(null);
  }

  async function refresh() {
    const fresh = await getMe();
    setUser(fresh);
    localStorage.setItem('rfo_user', JSON.stringify(fresh));
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, refresh, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
